import jwt_decode from "jwt-decode";
import Config from "../config/index";
import Api from "../core/Api";
import EventBus from "../core/EventBus";
import {
  getLocaleStorageItem,
  isProduction,
  polishedPictureUrl,
  setLocaleStorageItem
} from "../core/utils/index";
import { loadLanguageAsync } from "../plugins/i18n";
import {
  LOGIN_SUCCESS,
  LOGOUT,
  POPULATE_AZC_ACCESS_TOKEN,
  POPULATE_AZC_REFRESH_TOKEN,
  POPULATE_GAME,
  POPULATE_LOCALE,
  POPULATE_TOKEN
} from "./mutation_types";

export default {
  /**
   * loginAZC
   * Login our user using azerion connect, set a JWT token and set logged in state.
   * @param {Promise} store
   * @param {object} info
   * @return {Promise}
   */
  loginUser: (store, info) => {
    return new Promise((resolve, reject) => {
      Api.loginUserAzc({
        locale: store.getters.getLocale,
        domain: store.getters.getDomain,
        platform: store.getters.getGame.platform,
        ...info
      })
        .then(async response => {
          if (response.success) {
            const data = jwt_decode(response.data);
            window.localStorage.setItem("gd_plus_token", response.data);
            store.commit(POPULATE_TOKEN);
            window.localStorage.setItem(
              "gd_plus_azc_access_token",
              info.azc_access_token
            );
            store.commit(POPULATE_AZC_ACCESS_TOKEN);
            window.localStorage.setItem(
              "gd_plus_azc_refresh_token",
              info.azc_refresh_token
            );
            store.commit(POPULATE_AZC_REFRESH_TOKEN);

            const userInfo = await Api.fetchUserInfo().then(
              response => response.data
            );

            const user = {
              uid: data.uid,
              sid: `psgn:${data.sub}`,
              country: data.country,
              role: data.role,
              is_guest: false,
              username: data.username,
              email: data.email,
              registeredAt: userInfo.created,
              picture: polishedPictureUrl(userInfo.picture),
              friendsList: []
            };

            // Added temporarily for azerion connect so that people
            // ToDo: get rid of it once we're done with email change
            user.canChangeEmail = info.registration_source === "Plinga";

            window.localStorage.setItem("gd_plus_user", JSON.stringify(user));
            store.commit(LOGIN_SUCCESS);

            // It is needed to send registration event to gamedock
            const isNewRegisteredUser = data.newUser;
            resolve(isNewRegisteredUser);
          } else {
            reject(response.error);
          }
        })
        .catch(err => {
          const response = err.response ? err.response : null;
          if (!response) return reject(err.message);
          reject(response.data.error);
        });
    });
  },

  /**
   * populateUser
   * Consume the token to initiate the user for register
   * @param {Promise} store
   * @return {Promise}
   */
  populateUser: async (store, token) => {
    const data = jwt_decode(token);
    window.localStorage.setItem("gd_plus_token", token);
    store.commit(POPULATE_TOKEN);

    const info = await Api.fetchUserInfo().then(response => response.data);

    const user = {
      uid: data.uid,
      sid: `psgn:${data.sub}`,
      country: data.country,
      role: data.role,
      is_guest: false,
      email: data.email,
      username: data.username,
      registeredAt: info.created,
      picture: polishedPictureUrl(info.picture),
      friendsList: []
    };
    window.localStorage.setItem("gd_plus_user", JSON.stringify(user));
    store.commit(LOGIN_SUCCESS);
  },

  /**
   * loginGuest
   * Login our guest user, set a JWT token and set logged in state.
   * @param {Promise} store
   * @return {Promise}
   */
  loginGuest: store => {
    return new Promise((resolve, reject) => {
      Api.loginGuest({
        locale: store.getters.getLocale,
        domain: store.getters.getDomain
      })
        .then(response => {
          const data = jwt_decode(response.data);
          window.localStorage.setItem("gd_plus_token", response.data);
          window.localStorage.setItem(
            "gd_plus_user",
            JSON.stringify({
              uid: data.uid,
              country: data.country,
              role: data.role,
              is_guest: true,
              username: "Guest",
              friendsList: []
            })
          );
          store.commit(LOGIN_SUCCESS);
          resolve();
        })
        .catch(err => reject(err.message));
    });
  },

  /**
   * logoutUser
   * Logout the user.
   * @param {Object} store
   * @param {Boolean} azcLogout
   */
  logoutUser: (store, azcLogout) => {
    // Commit LOGOUT mutation that will reset our credential store settings.
    return new Promise(resolve => {
      // window.location.href = `/${store.getters.getGame.gameName}`;
      if (azcLogout) {
        Api.logutAzc({
          azc_access_token: store.getters.getAzcAccessToken,
          azc_refresh_token: store.getters.getAzcRefreshToken
        })
          .then(() => {
            store.commit(LOGOUT);
            new EventBus().broadcast("GAME_RELOAD");
            resolve({});
          })
          .catch(err => {
            console.log(err);
            store.commit(LOGOUT);
            resolve(err);
          });
      } else {
        store.commit(LOGOUT);
        new EventBus().broadcast("GAME_RELOAD");
        resolve();
      }
    });
  },

  /**
   * fetchUserInfo
   */
  fetchUserInfo: () => {
    return new Promise((resolve, reject) => {
      Api.fetchUserInfo()
        .then(response => {
          if (response.success) {
            resolve(response.data);
          } else {
            resolve({});
          }
        })
        .catch(err => {
          console.log(err);
          reject(err);
        });
    });
  },

  /**
   * fetchGameConfig
   * Fetches the frontend related configs of the game.
   * @param {Object} store
   */
  fetchGameConfig: (store, game) => {
    return new Promise((resolve, reject) => {
      // Remove the other game's config on storage
      // In case we have different one now
      if (
        game.toLowerCase() !== store.getters.getGame?.gameName.toLowerCase()
      ) {
        window.localStorage.removeItem("gd_plus_game");
      }

      // If there is a game config on storage and not expired
      // simply return it
      const storedGame = getLocaleStorageItem("gd_plus_game");
      if (storedGame) return resolve(storedGame);

      Api.fetchGameConfig(game, store.getters.getDomain)
        .then(response => {
          if (response.success) {
            const gameConfig = response.data;
            gameConfig.isProduction = isProduction;

            // "Set" game config with 5 mins expire time
            setLocaleStorageItem(
              "gd_plus_game",
              JSON.stringify(gameConfig),
              new Date(new Date().getTime() + 5 * 60000)
            );

            store.commit(POPULATE_GAME);
            resolve(true);
          } else {
            resolve(false);
          }
        })
        .catch(err => {
          console.log(err);
          reject(err);
        });
    });
  },

  /**
   * fetchFriends
   * Fetches the friends of the user in the game.
   * @param {Object} store
   */
  fetchFriends: async store => {
    return Api.fetchFriends(store.getters.getGame.plingaGameId).then(
      response => {
        const user = store.getters.getUser;
        user.friendsList = response.data;

        user.friendsList.forEach(friend => {
          // We're replacing old system default avatar with new one on s3
          if (
            friend.picture &&
            friend.picture.indexOf("assets/img/avatars/") > -1
          ) {
            friend.picture = `${Config.ASSET_URL}/avatar/01.png`;
          }

          friend.picture = polishedPictureUrl(friend.picture);
        });

        window.localStorage.setItem("gd_plus_user", JSON.stringify(user));
        store.state.user = user;
      }
    );
  },

  /**
   * fetchFriendRequests
   * Fetches the list of the users who invited you.
   * @param {Object} store
   */
  fetchFriendRequests: store => {
    return new Promise(resolve => {
      Api.fetchFriendRequests(store.getters.getGame.plingaGameId)
        .then(response => {
          if (response.success) {
            resolve(response.data);
          } else {
            //reject(response.error);
            resolve([]);
          }
        })
        .catch(err => {
          if (!isProduction) console.log(err);
          else console.log(err.message);
          resolve([]);
        });
    });
  },

  /**
   * fetchPlayingUsers
   * Fetches the list of the users who play the game.
   * @param {Object} store
   */
  fetchPlayingUsers: store => {
    return new Promise((resolve, reject) => {
      const platformFriendsOnly = store.getters.getGame.platformFriendsOnly;
      const excludedFriendPlatforms =
        store.getters.getGame.excludedFriendPlatforms;

      const queryParams = {
        platformFriendsOnly: platformFriendsOnly ?? false
      };

      if (platformFriendsOnly) {
        queryParams.platform = store.getters.getGame.platform;
      } else if (excludedFriendPlatforms) {
        queryParams.excludedPlatforms = excludedFriendPlatforms;
      }

      Api.fetchPlayingUsers(store.getters.getGame.plingaGameId, queryParams)
        .then(response => {
          if (response.success) resolve(response.data);
          else {
            console.log(response.error);
            reject([]);
          }
        })
        .catch(err => {
          if (!isProduction) console.log(err);
          else console.log(err.message);
          resolve([]);
        });
    });
  },

  /**
   * inviteUser
   * Invite a user to be friends in the game.
   * @param {Object} store
   * @param {Number} userId
   */
  inviteUser: (store, userId) => {
    return new Promise((resolve, reject) => {
      Api.inviteUser(store.getters.getGame.plingaGameId, userId)
        .then(response => {
          if (response.success) resolve();
          else reject(response.error);
        })
        .catch(err => {
          if (!isProduction) console.log(err);
          else console.log(err.message);
          reject(err.message);
        });
    });
  },

  /**
   * inviteUserViaEmail
   * Invite a user to play the game.
   * @param {Object} store
   * @param {Object} params
   */
  inviteUserViaEmail: (store, params) => {
    return new Promise((resolve, reject) => {
      Api.inviteUserViaEmail({
        ...params,
        gameName: store.getters.getGame.niceGameName,
        backendName: store.getters.getGame.gameName,
        gameId: store.getters.getGame.plingaGameId
      })
        .then(response => {
          if (response.success) resolve();
          else reject(response.error);
        })
        .catch(err => {
          if (!isProduction) console.log(err);
          else console.log(err.message);
          reject(err.message);
        });
    });
  },

  /**
   * acceptFriendRequest
   * Accepts the friend request coming from a user.
   * @param {Object} store
   * @param {Number} friendId
   */
  acceptFriendRequest: (store, friendId) => {
    return new Promise((resolve, reject) => {
      Api.acceptFriendRequest(store.getters.getGame.plingaGameId, friendId)
        .then(response => {
          if (response.success) resolve();
          else reject(response.error);
        })
        .catch(err => {
          if (!isProduction) console.log(err);
          else console.log(err.message);
          reject(err.message);
        });
    });
  },

  /**
   * declineFriendRequest
   * Declines the friend request coming from a user.
   * @param {Object} store
   * @param {Number} friendId
   */
  declineFriendRequest: (store, friendId) => {
    return new Promise((resolve, reject) => {
      Api.declineFriendRequest(store.getters.getGame.plingaGameId, friendId)
        .then(response => {
          if (response.success) resolve();
          else reject(response.error);
        })
        .catch(err => {
          if (!isProduction) console.log(err);
          else console.log(err.message);
          reject(err.message);
        });
    });
  },

  /**
   * deleteFriend
   * Deletes a friend from friend list
   * @param {Object} store
   * @param {Number} friendId
   */
  deleteFriend: (store, friendId) => {
    return new Promise((resolve, reject) => {
      Api.deleteFriend(store.getters.getGame.plingaGameId, friendId)
        .then(response => {
          if (response.success) resolve();
          else reject(response.error);
        })
        .catch(err => {
          if (!isProduction) console.log(err);
          else console.log(err.message);
          reject(err.message);
        });
    });
  },

  /**
   * fetchUserDownloadableDataLink
   * Fetch all user data of the user
   */
  fetchUserDownloadableDataLink: () => {
    return new Promise((resolve, reject) => {
      Api.fetchUserDownloadableDataLink()
        .then(response => {
          if (response.success) resolve(response.data.link);
          else reject(response.error);
        })
        .catch(err => {
          if (!isProduction) console.log(err);
          else console.log(err.message);
          reject(err.message);
        });
    });
  },
  /**
   * searchPlayingUser
   * @param {Object} params
   * @param {store} params
   */
  searchPlayingUser: (store, searchQuery) => {
    return new Promise((resolve, reject) => {
      let gameId = store.getters.getGame.plingaGameId;
      const platformFriendsOnly = store.getters.getGame.platformFriendsOnly;
      const excludedFriendPlatforms =
        store.getters.getGame.excludedFriendPlatforms;

      const queryParams = {
        platformFriendsOnly: platformFriendsOnly ?? false,
        query: searchQuery
      };

      if (platformFriendsOnly) {
        queryParams.platform = store.getters.getGame.platform;
      } else if (excludedFriendPlatforms) {
        queryParams.excludedPlatforms = excludedFriendPlatforms;
      }

      Api.searchPlayingUser(gameId, queryParams)
        .then(response => {
          if (response.success) resolve(response.data);
          else reject(response.error);
        })
        .catch(err => {
          if (!isProduction) console.log(err);
          else console.log(err.message);
          reject(err.message);
        });
    });
  },

  /**
   * saveSettings
   * Save user preferences on settings component
   * @param {Object} store
   */
  saveSettings: (store, settings) => {
    return new Promise((resolve, reject) => {
      const isLanguageChanged = store.getters.getLocale !== settings.locale;
      loadLanguageAsync(settings.locale);
      window.localStorage.setItem("gd_plus_locale", settings.locale);
      store.commit(POPULATE_LOCALE, settings.locale);

      Api.updateUserSettings(settings)
        .then(() => {
          // if the language is changed,
          // we need to reload the game with correct language
          if (isLanguageChanged) new EventBus().broadcast("GAME_RELOAD");
          resolve();
        })
        .catch(err => {
          const response = err.response ? err.response : null;
          if (!response) return reject(err.message);
          reject(response.data.error);
        });
    });
  },

  /**
   * updateEmail/Request
   * send code to change e-mail address
   * @param {Object} store
   * @param {Object} params
   */
  updateEmailRequest: (store, params) => {
    return new Promise((resolve, reject) => {
      Api.updateEmailRequest({
        ...params,
        gameName: store.getters.getGame.niceGameName,
        gameBackendName: store.getters.getGame.gameName,
        locale: store.getters.getLocale
      })
        .then(response => {
          if (response.success) {
            resolve(response.data);
          } else {
            reject(response.error);
          }
        })
        .catch(err => {
          const response = err.response ? err.response : null;
          if (!response) return reject(err.message);
          reject(response.data.error);
        });
    });
  },
  /**
   * updateEmail
   * send code to change e-mail address
   * @param {Object} store
   * @param {Object} params
   */
  updateEmail: (store, params) => {
    return new Promise((resolve, reject) => {
      Api.updateEmail({
        ...params
      })
        .then(response => {
          if (response.success) {
            resolve(response.data);
          } else {
            reject(response.error);
          }
        })
        .catch(err => {
          const response = err.response ? err.response : null;
          if (!response) return reject(err.message);
          reject(response.data.error);
        });
    });
  },

  /**
   * changeLanguage
   * Just change the language
   * @param {Object} store
   */
  changeLanguage: (store, locale) => {
    loadLanguageAsync(locale);
    window.localStorage.setItem("gd_plus_locale", locale);
    store.commit(POPULATE_LOCALE, locale);
  },

  /**
   * sendZendeskTicket
   * send ticket to zendesk
   * @param {Object} params
   */
  sendZendeskTicket: (store, params) => {
    return new Promise((resolve, reject) => {
      Api.sendZendeskTicket(params)
        .then(() => {
          resolve();
        })
        .catch(err => {
          console.log(err);
          reject(err.message);
        });
    });
  },

  /**
   * See Notification
   * @param {Object} store
   * @param {Object} params
   */
  seeNotification: (store, params) => {
    return new Promise((resolve, reject) => {
      Api.seeNotification(params)
        .then(response => {
          if (response.success) resolve(response);
          else reject(response.error);
        })
        .catch(err => {
          if (!isProduction) console.log(err);
          else console.log(err.message);
          reject(err.message);
        });
    });
  },

  /**
   * fetchNotifications
   * @param {Object} store
   * @param {Object} params
   */
  fetchNotifications: (store, params) => {
    let body = {
      gameName: store.getters.getGame.gameName,
      locale: store.getters.getLocale,
      page: params
    };
    return new Promise((resolve, reject) => {
      Api.fetchNotifications(body)
        .then(response => {
          resolve(response.data);
        })
        .catch(err => {
          console.log(err);
          reject(err.message);
        });
    });
  },
  /**
   * Delete Notification
   * @param {Object} store
   * @param {Object} params
   */
  deleteNotifications: (store, params) => {
    console.log("paramsaction", params);
    return new Promise((resolve, reject) => {
      Api.deleteNotifications(params)
        .then(response => {
          if (response.success) resolve(response);
          else reject(response.error);
        })
        .catch(err => {
          if (!isProduction) console.log(err);
          else console.log(err.message);
          reject(err.message);
        });
    });
  },
  /**
   * Get Game Notification Total Unseen
   * @param {Object} store
   */
  gameNotificationTotalUnseen: store => {
    let body = {
      gameName: store.getters.getGame.gameName
    };
    return new Promise((resolve, reject) => {
      Api.gameNotificationTotalUnseen(body)
        .then(response => {
          resolve(response.data);
        })
        .catch(err => {
          console.log(err);
          reject(err.message);
        });
    });
  }
};
